import { useI18n } from "../../../i18n";
import { Goal } from "./ScenePanels";
import type { Sensors } from "../lib/live";
import type { SensorScene, SensorState } from "../lib/sensorWorld";

/**
 * 센서 월드 판(5단계 · 안내형) — 구역 길 · 지금 구역의 안내(어느 슬라이더를 어디로) · 지금 센서 값 · 보드 반응 · 목표.
 * 센서 값이 오지 않으면(진짜 보드) '보드로 해 봐요' 안내 — 그때는 보드 반응만 본다(lib/sensorWorld stepSensor).
 */
export function SensorPanel({ scene, s, sensors, boardLabel }: { scene: SensorScene; s: SensorState; sensors: Sensors | undefined; boardLabel: string }) {
  const { t } = useI18n();
  const zone = scene.zones[Math.min(s.zone, scene.zones.length - 1)]!;
  const total = scene.repeat ?? scene.zones.length;
  // 이 장면이 쓰는 센서의 지금 값(온도 × 빛이면 둘 다) — 센서 없는 구역(자유낙하 · 자석 · 다시 보기)은 보드 반응만
  const shown = scene.sensors.filter((k) => sensors?.[k] !== undefined).map((k) => t(`student.microbit.sensor.value.${k}`, { v: sensors![k]! }));
  const needsValue = !!zone.sensor;
  return (
    <div className="pf-mb__scene" data-testid={`fs-mb-scene-${scene.id}`}>
      {scene.banner ? <p className="pf-mb__banner" role="note" data-testid="fs-mb-sensor-banner">{t(`student.microbit.sensor.banner.${scene.id}`)}</p> : null}
      <div className="pf-mb__row pf-mb__row--start" aria-label={t("student.microbit.sensor.path")}>
        {scene.repeat
          ? Array.from({ length: scene.repeat }, (_, i) => <span key={i} className="pf-mb__big" data-on={i < s.count || undefined}>{i < s.count ? "🌸" : "🌱"}</span>)
          : scene.zones.map((zz, i) => <span key={zz.key} className="pf-mb__chip" data-on={i < s.zone || undefined}>{zz.emoji} {t(`student.microbit.sensor.zone.${zz.key}.name`)}{i < s.zone ? " ✓" : ""}</span>)}
      </div>
      {!s.done ? (
        <p className="pf-mb__bubble" data-testid="fs-mb-sensor-ask">{zone.emoji} {t(`student.microbit.sensor.zone.${zone.key}.ask`)}</p>
      ) : (
        <p className="pf-mb__bubble" data-testid="fs-mb-sensor-ask">🎉 {t("student.microbit.sensor.done")}</p>
      )}
      <p className="pf-mb__scene-state" data-testid="fs-mb-sensor-value">
        {shown.length ? `${shown.join(" · ")} · ` : needsValue ? `${t("student.microbit.sensor.noValue")} · ` : ""}
        {t("student.microbit.ai.board", { board: boardLabel || "—" })}
        {zone.expect.kind === "shakes" && !s.done ? ` · ${t("student.microbit.sensor.shakes", { n: Math.min(s.hits, zone.expect.n), goal: zone.expect.n })}` : ""}
      </p>
      {!s.done && s.held > 0 ? (
        <div className="pf-mb__bar" role="progressbar" aria-valuemin={0} aria-valuemax={zone.holdMs} aria-valuenow={s.held}>
          <span className="pf-mb__bar-fill" style={{ width: `${Math.min(100, (s.held / zone.holdMs) * 100)}%` }} />
        </div>
      ) : null}
      <Goal id="fs-mb-scene-goal" done={s.done} text={t(`student.microbit.scene.${scene.id}.goal`, { n: Math.min(s.count, total), goal: total })} />
    </div>
  );
}
